import React, { ReactElement } from 'react';
import { Avatar, Button, Flex, Text, VStack } from '@chakra-ui/react';
import { ChevronRightIcon, QrCodeIcon, DeleteIcon, EmailIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';

type Student = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

type Group = {
  id: string;
  name: string;
  isConnectedToWorksheet: boolean;
  students?: Student[];
};

type Props = {
  group: Group;
  isExpanded: boolean;
  loadingGroupId: string | null;
  onToggleExpansion: (groupId: string) => void;
  onQrShare: (group: { name: string }) => void;
  onRemove: (groupId: string) => void;
  onAdd: (groupId: string) => void;
};

export const GroupListItem = ({
  group,
  isExpanded,
  loadingGroupId,
  onToggleExpansion,
  onQrShare,
  onRemove,
  onAdd,
}: Props): ReactElement => {
  const studentCount = group.students?.length || 0;

  return (
    <Flex key={group.id} direction="column" width="100%">
      <Flex
        direction="column"
        gap="15px"
        bg="background.primary"
        pb={0}
        pt="15px"
        px="10px"
        width="100%"
      >
        <Flex justify="space-between" align="center" width="100%">
          <Flex flex="1" direction="column" gap="9px" minW="0">
            <Flex gap="6px" align="center">
              <Flex
                as="button"
                width="14px"
                height="14px"
                align="center"
                justify="center"
                transform={isExpanded ? 'rotate(90deg)' : 'rotate(270deg)'}
                transition="transform 0.2s"
                cursor={studentCount > 0 ? 'pointer' : 'default'}
                onClick={() => studentCount > 0 && onToggleExpansion(group.id)}
                opacity={studentCount > 0 ? 1 : 0.5}
              >
                <ChevronRightIcon boxSize="14px" color="text.secondary" />
              </Flex>
              <Text textStyle="body.m.medium" color="text.primary" lineClamp={1}>
                {group.name}
              </Text>
            </Flex>
            <Flex pl="l" width="100%">
              <Text textStyle="body.s.regular" color="text.tertiary" flex="1" lineClamp={1}>
                {studentCount} Lernende
              </Text>
            </Flex>
          </Flex>
          <Flex gap="10px" align="center">
            {group.isConnectedToWorksheet && (
              <Flex
                as="button"
                width="18px"
                height="18px"
                align="center"
                justify="center"
                cursor="pointer"
                _hover={{ opacity: '70' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onQrShare({ name: group.name });
                }}
              >
                <NovaIcon icon={QrCodeIcon} size="m" color="icon.secondary" aria-hidden />
              </Flex>
            )}
            {group.isConnectedToWorksheet ? (
              <Flex
                as="button"
                width="18px"
                height="18px"
                align="center"
                justify="center"
                cursor="pointer"
                _hover={{ opacity: '70' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(group.id);
                }}
                opacity={loadingGroupId === group.id ? 0.5 : 1}
                pointerEvents={loadingGroupId === group.id ? 'none' : 'auto'}
              >
                <NovaIcon icon={DeleteIcon} size="m" color="icon.error" aria-hidden />
              </Flex>
            ) : (
              <Button
                bg="background.primary"
                borderWidth="1px"
                borderColor="border.accent"
                borderRadius="m"
                px="15px"
                py="xs"
                minW="auto"
                height="auto"
                onClick={(e) => {
                  e.stopPropagation();
                  onAdd(group.id);
                }}
                loading={loadingGroupId === group.id}
                _hover={{ opacity: '80' }}
              >
                <Text textStyle="body.s.regular" color="text.secondary">
                  Teilen
                </Text>
              </Button>
            )}
          </Flex>
        </Flex>
        <Flex height="0" width="100%" position="relative">
          <Flex
            position="absolute"
            top="-0.5px"
            left="0"
            right="0"
            height="1px"
            borderColor="border.accent"
          />
        </Flex>
      </Flex>

      {isExpanded && group.students && group.students.length > 0 && (
        <VStack gap={0} align="stretch" width="100%" pl="30px">
          {group.students.map((student: Student) => (
            <Flex
              key={student.id}
              direction="column"
              gap="15px"
              bg="background.primary"
              pb={0}
              pt="15px"
              px="10px"
              width="100%"
            >
              <Flex gap="l" align="flex-start" width="100%">
                <Flex flex="1" direction="column" gap="10px" minW="0">
                  <Flex gap="xs" align="center">
                    <Avatar.Root
                      width="28px"
                      height="28px"
                      bg="background.highlight.blue"
                      color="text.onAccent"
                      fontSize="13.54px"
                    >
                      <Avatar.Fallback name={`${student.firstName} ${student.lastName}`} />
                    </Avatar.Root>
                    <Text textStyle="body.s.medium" color="text.primary">
                      {student.firstName} {student.lastName}
                    </Text>
                  </Flex>
                  <Flex gap="5px" align="center" pl="36px">
                    <EmailIcon width="13.5px" height="13.5px" color="icon.tertiary" />
                    <Text textStyle="body.s.regular" color="text.tertiary">
                      {student.email}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
              <Flex height="1px" width="100%" borderColor="border.accent" />
            </Flex>
          ))}
        </VStack>
      )}
    </Flex>
  );
};
